import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, TrendingDown, TrendingUp, Minus, Leaf } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";

const KIND_LABELS = {
  phq9: "PHQ-9 (Depresión)",
  gad7: "GAD-7 (Ansiedad)",
  ghq28: "GHQ-28 (Salud general)",
  bhs: "BHS (Desesperanza)",
  swls: "SWLS (Satisfacción)",
  perma: "PERMA (Bienestar)",
};

// For most scales, lower = better. For SWLS/PERMA, higher = better.
const HIGHER_IS_BETTER = { swls: true, perma: true };

export default function Progreso() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/questionnaires").then(r => setData(r.data)).catch(() => setData([]));
  }, []);

  const grouped = useMemo(() => {
    const map = {};
    (data || []).forEach(q => {
      map[q.kind] = map[q.kind] || {};
      map[q.kind][q.phase || "baseline"] = q;
    });
    return map;
  }, [data]);

  const chartData = Object.entries(grouped).map(([kind, phases]) => ({
    name: KIND_LABELS[kind] || kind,
    Inicial: phases.baseline?.score ?? null,
    Final: phases.final?.score ?? null,
  }));

  const hasAnyFinal = chartData.some(d => d.Final !== null);

  const trend = (kind) => {
    const b = grouped[kind]?.baseline?.score;
    const f = grouped[kind]?.final?.score;
    if (b == null || f == null) return null;
    const higherBetter = HIGHER_IS_BETTER[kind];
    if (f === b) return { icon: Minus, color: "var(--ha-muted)", label: "Sin cambio" };
    const improved = higherBetter ? f > b : f < b;
    return improved
      ? { icon: TrendingUp, color: "var(--ha-success)", label: "Mejoraste" }
      : { icon: TrendingDown, color: "var(--ha-error)", label: "Empeoró" };
  };

  return (
    <main className="min-h-screen bg-[var(--ha-bg)]" data-testid="progreso-page">
      <header className="max-w-5xl mx-auto flex items-center justify-between px-6 py-6">
        <div className="flex items-center gap-2">
          <div className="h-10 w-10 rounded-full bg-[var(--ha-primary)] grid place-items-center shadow-sm">
            <Leaf className="w-5 h-5 text-white" strokeWidth={1.5} />
          </div>
          <span className="font-extrabold tracking-tight text-[var(--ha-text)] text-lg">MINDHOME HEALTH APP</span>
        </div>
        <Button data-testid="back-dashboard-btn" variant="outline" onClick={() => navigate("/dashboard")} className="rounded-full border-[var(--ha-border)] h-9 px-4">
          <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} /> Volver al panel
        </Button>
      </header>

      <section className="max-w-5xl mx-auto px-6 py-6">
        <p className="text-xs uppercase tracking-[0.2em] text-[var(--ha-muted)] font-semibold">Comparativa Pre y Post</p>
        <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold tracking-tight text-[var(--ha-text)]">Tu progreso a lo largo del programa</h1>
        <p className="mt-3 text-[var(--ha-muted)] max-w-2xl">Compara las puntuaciones de la <strong>Sesión 1</strong> (línea base) con las de la <strong>Sesión 8</strong> (evaluación final).</p>

        {!hasAnyFinal && (
          <Card className="mt-8 rounded-2xl border-[var(--ha-secondary)]/30 bg-[var(--ha-secondary)]/5" data-testid="progreso-empty">
            <CardContent className="p-6">
              <p className="font-semibold text-[var(--ha-text)]">Aún no has completado la evaluación final.</p>
              <p className="mt-2 text-sm text-[var(--ha-muted)]">Realiza la <strong>Sesión 8</strong> para desbloquear la comparativa completa.</p>
              <Button data-testid="go-session-8-btn" onClick={() => navigate("/sesion/8")} className="mt-4 rounded-full bg-[var(--ha-primary)] hover:bg-[var(--ha-primary-hover)] text-white h-11 px-6">
                Ir a la Sesión 8
              </Button>
            </CardContent>
          </Card>
        )}

        <Card className="mt-8 rounded-2xl border-[var(--ha-border)] bg-white">
          <CardContent className="p-6">
            <p className="text-xs uppercase tracking-[0.14em] text-[var(--ha-muted)] font-bold">Puntuaciones inicial vs final</p>
            <div className="mt-4 h-80">
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--ha-border)" />
                    <XAxis dataKey="name" stroke="var(--ha-muted)" fontSize={11} interval={0} angle={-15} textAnchor="end" height={70} />
                    <YAxis stroke="var(--ha-muted)" fontSize={11} />
                    <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid var(--ha-border)" }} />
                    <Legend />
                    <Bar dataKey="Inicial" fill="var(--ha-accent)" radius={[8, 8, 0, 0]} />
                    <Bar dataKey="Final" fill="var(--ha-primary)" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-sm text-[var(--ha-muted)] text-center pt-20">Realiza al menos la Sesión 1 para ver tus puntuaciones.</p>
              )}
            </div>
          </CardContent>
        </Card>

        {Object.keys(grouped).length > 0 && (
          <div className="mt-8 grid md:grid-cols-2 gap-4" data-testid="progreso-cards">
            {Object.entries(grouped).map(([kind, phases]) => {
              const t = trend(kind);
              const Icon = t?.icon;
              return (
                <Card key={kind} className="rounded-2xl border-[var(--ha-border)] bg-white" data-testid={`compare-${kind}`}>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <p className="font-bold text-[var(--ha-text)]">{KIND_LABELS[kind] || kind}</p>
                      {Icon && (
                        <span className="flex items-center gap-1.5 text-xs font-semibold" style={{ color: t.color }}>
                          <Icon className="w-4 h-4" strokeWidth={2} /> {t.label}
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="rounded-xl border border-[var(--ha-border)] p-3">
                        <p className="text-xs text-[var(--ha-muted)] font-semibold uppercase tracking-wide">Inicial</p>
                        <p className="mt-1 text-2xl font-extrabold text-[var(--ha-accent)]">{phases.baseline?.score ?? "—"}</p>
                        <p className="text-xs text-[var(--ha-muted)]">{phases.baseline?.interpretation ?? ""}</p>
                      </div>
                      <div className="rounded-xl border border-[var(--ha-border)] p-3">
                        <p className="text-xs text-[var(--ha-muted)] font-semibold uppercase tracking-wide">Final</p>
                        <p className="mt-1 text-2xl font-extrabold text-[var(--ha-primary)]">{phases.final?.score ?? "—"}</p>
                        <p className="text-xs text-[var(--ha-muted)]">{phases.final?.interpretation ?? "Pendiente"}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </section>
    </main>
  );
}
